import CloseIcon from '@mui/icons-material/Close';
import { yupResolver } from '@hookform/resolvers/yup';
import ErrorIcon from '@mui/icons-material/Error';
import { CircularProgress, InputAdornment, Typography } from '@mui/material';
import { useState } from 'react';
import Modal from 'react-modal';
import { IUpdateSolicitationInputModel } from 'inputModels';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { serialize } from 'object-to-formdata';
import * as yup from 'yup';
import { v4 as uuid } from 'uuid';
import { useSolicitation } from '../../../context/solicitation';
import { Attachment } from '../../Attachment';
import { CustomFile, DropZone } from '../../DropZone';
import { ContentModal, Header } from './styles';
import { CorrespondenceType, ServiceStatus } from '../../../types/enums';
import { SolicitationController } from '../../../controller/SolicitationController';
import { ReactComponent as IconCheck } from '../../../assets/icons/check-message-read.svg';
import { Button } from '../../Buttons/Button';
import CurrencyInput from '../../CurrencyInput';
import { Input } from '../../Inputs/Input';
import { DropDownList } from '../../DropDownList';

interface ModalFinalizeDemandProps {
  isOpen: boolean;
  onRequestClose: () => void;
  solicitationId: string;
  typeCorrespondenceService: CorrespondenceType;
}
export function ModalFinalizeDemand({
  isOpen,
  solicitationId,
  onRequestClose,
  typeCorrespondenceService,
}: ModalFinalizeDemandProps) {
  const registerSchema = yup.object({
    finalizationDetails: yup.string(),
    attachments: yup.mixed().test('hasFile', 'Anexe um arquivo para concluir a demanda', () => {
      return !!attachments;
    }),
    isIntegratedProtocol: yup.boolean().required('Campo obrigatório'),
    integratedProtocolValue: yup.number().when('isIntegratedProtocol', {
      is: true,
      then: yup.number().min(1, 'Valor mínimo de R$1,00').required('Informe o valor'),
    }),
    quantity: yup.number().when('isIntegratedProtocol', {
      is: true,
      then: yup
        .number()
        .typeError('Informe um número')
        .min(1, 'Quantidade mínima é 1')
        .max(999, 'Quantidade máxima é 999'),
    }),
  });
  const { setDemands, demands } = useSolicitation();
  const [attachments, setAttachments] = useState<CustomFile[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [defaultValues] = useState({
    details: '',
    isIntegratedProtocol: false,
    integratedProtocolValue: 0,
    quantity: 1,
    attachments,
  });
  const {
    handleSubmit,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(registerSchema),
  });

  const [finalizationDetails, isIntegratedProtocol, integratedProtocolValue] = useWatch({
    control,
    name: ['details', 'isIntegratedProtocol', 'integratedProtocolValue'],
  });
  const handleRequestClose = () => {
    if (isLoading) return;
    setAttachments([]);
    setValue('details', '');
    setValue('isIntegratedProtocol', false);
    setValue('integratedProtocolValue', 0);
    setValue('quantity', 1);
    clearErrors();
    onRequestClose();
  };

  const handleCompleteSolicitation = () => {
    if (attachments?.length === 0) return;

    setIsLoading(true);
    const demand = demands.find((s) => s.id === solicitationId);
    let status: ServiceStatus = ServiceStatus.IN_APPROVAL_REQUESTER;
    if (demand) {
      const hasApprover = !demand.createdBy?.collaboratorInfo?.profile?.approversId
        ? false
        : demand.createdBy?.collaboratorInfo?.profile?.approversId.length > 0;
      status = hasApprover
        ? ServiceStatus.IN_APPROVAL_APPROVER
        : ServiceStatus.IN_APPROVAL_REQUESTER;
    }

    const update: IUpdateSolicitationInputModel = {
      solicitationId,
      status,
      finalizationDetails,
      integratedProtocolValue,
      isIntegratedProtocol,
    };

    const formData = serialize(update, { indices: true });

    attachments?.forEach((attachment) => {
      if (attachment.file) {
        formData.append('finalizationAttachments', attachment.file);
      }
    });

    SolicitationController.updateStatus(formData)
      .then(() => {
        setDemands((prev) => {
          const idxDemand = prev.findIndex((d) => d.id === solicitationId);
          const newDemands = [...prev];
          if (idxDemand !== -1) {
            newDemands[idxDemand].integratedProtocolValue = update.integratedProtocolValue;
            newDemands[idxDemand].isIntegratedProtocol = update.isIntegratedProtocol;
            newDemands[idxDemand].status = status;
            newDemands[idxDemand].finalizationDetails = update.finalizationDetails;
            newDemands[idxDemand].finalizationAttachments = attachments?.map((attachment) => ({
              id: uuid(),
              contentType: attachment.file.type,
              name: attachment.file.name,
              file: attachment.file,
              url: attachment.preview,
            }));
          }
          return newDemands;
        });
      })
      .finally(() => {
        setIsLoading(false);
        handleRequestClose();
      });
  };

  const handleInsertAttachment = (files: CustomFile[]) => {
    setAttachments((prev) => [...(prev || []), ...files]);
    clearErrors('attachments');
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleRequestClose}
      overlayClassName="react-modal-overlay"
      className="react-finalize-demand-modal-content">
      <Header>
        <h1>
          <IconCheck /> Concluir Demanda
        </h1>
        <div className="close-div">
          <button type="button" onClick={handleRequestClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </Header>
      <ContentModal>
        {typeCorrespondenceService === CorrespondenceType.PROTOCOL && (
          <div className="containerIntegratedProtocol">
            <h3>É um protocolo integrado?</h3>
            <div className="controllerYesNo">
              <Controller
                control={control}
                name="isIntegratedProtocol"
                render={({ field: { onChange } }) => (
                  <DropDownList
                    itens={[
                      { id: 'Sim', value: 'Sim' },
                      { id: 'Não', value: 'Não' },
                    ]}
                    defaultValue="Não"
                    typeReturn="id"
                    onChange={(value) => {
                      onChange(value === 'Sim');
                      setValue('integratedProtocolValue', 0);
                      setValue('quantity', 1);
                    }}
                  />
                )}
              />
            </div>
          </div>
        )}
        {isIntegratedProtocol && (
          <div style={{ display: 'flex', gap: '3rem' }}>
            <div className="containerIntegratedProtocol">
              <h4>Valor</h4>
              <Controller
                control={control}
                name="integratedProtocolValue"
                render={({ field: { onChange, value } }) => (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                    }}>
                    <CurrencyInput
                      value={value || 0}
                      fullWidth
                      variant="outlined"
                      size="small"
                      className="inputSuggestionValue"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment sx={{ pl: 1 }} position="start">
                            R$
                          </InputAdornment>
                        ),
                      }}
                      onChange={(qtd) => {
                        onChange(qtd);
                        setValue('quantity', 1);
                        clearErrors('quantity');
                      }}
                    />
                    {errors.integratedProtocolValue && (
                      <span className="fieldError" style={{ position: 'absolute', bottom: -25 }}>
                        <ErrorIcon fontSize="inherit" />
                        {errors.integratedProtocolValue.message}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
            <div className="containerIntegratedProtocol">
              <h4>Quantidade</h4>
              <div style={{ position: 'relative', display: 'flex', width: '100%' }}>
                <Input
                  name="quantity"
                  control={control}
                  variant="standard"
                  type="number"
                  width="10rem"
                  preventNotNumbers
                  autoComplete="off"
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (isNaN(value)) return;
                    if (value > 1 && isIntegratedProtocol && integratedProtocolValue) {
                      setValue('integratedProtocolValue', value * integratedProtocolValue);
                    }
                  }}
                />
                {errors.quantity && (
                  <span className="fieldError" style={{ position: 'absolute', bottom: -26 }}>
                    <ErrorIcon fontSize="inherit" />
                    {errors.quantity?.message}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="containerAttachment">
          <div className="contentAttachment">
            {isLoading ? (
              <div className="contentDropzone">
                <CircularProgress size={15} />
                <Typography
                  paddingLeft={1}
                  variant="caption"
                  color="text"
                  sx={{ fontStyle: 'italic' }}>
                  Carregando...
                </Typography>
              </div>
            ) : (
              <div className="contentDropzone">
                <DropZone
                  multipleFiles
                  type="attachments"
                  onFiles={(files) => handleInsertAttachment(files)}
                />
                <div className="attachments">
                  {attachments?.map((attachment) => (
                    <Attachment
                      key={attachment.id}
                      data={{
                        id: attachment.id,
                        type: attachment.file?.type ?? '',
                        name: attachment.file?.name ?? 'Sem nome',
                        uri: attachment.preview ?? undefined,
                      }}
                      onDelete={() => {
                        setAttachments((prev) => prev?.filter((att) => att.id !== attachment.id));
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          {errors.attachments?.message && (
            <p className="fieldError" style={{ display: 'flex', justifyContent: 'center' }}>
              <ErrorIcon />
              {errors.attachments?.message}
            </p>
          )}
        </div>
        <Controller
          control={control}
          name="details"
          render={({ field: { value, onChange, ...rest } }) => (
            <textarea
              value={value}
              onChange={onChange}
              {...rest}
              className="note"
              placeholder="Adicione suas observações"
            />
          )}
        />
        <div className="content-buttons">
          <Button
            text="Cancelar"
            dimensions={{ width: '12rem', height: '2rem' }}
            font={{ size: '14' }}
            color="background_white"
            background="primary"
            backgroundHoverColor="tertiary_500"
            textHoverColor="background_white"
            onClick={isLoading ? () => {} : handleRequestClose}
          />

          <Button
            text="Confirmar"
            loading={isLoading}
            dimensions={{ width: '12rem', height: '2rem' }}
            font={{ size: '14' }}
            color="background_white"
            background="secondary"
            backgroundHoverColor="tertiary_500"
            textHoverColor="background_white"
            border={{ color: 'primary' }}
            onClick={handleSubmit(handleCompleteSolicitation)}
          />
        </div>
      </ContentModal>
    </Modal>
  );
}
